import { z } from "zod";

import { isValidImageUrl } from "./image";
import { reservationStatusSchema } from "./reservation";

const dateTimeOrDateSchema = z
  .string()
  .datetime({ offset: true })
  .or(z.string().regex(/^\d{4}-\d{2}-\d{2}$/));

const customerEmailSchema = z.string().trim().toLowerCase().pipe(z.email().max(320));

export const storefrontAvailabilityInputSchema = z.object({
  startDate: dateTimeOrDateSchema,
  endDate: dateTimeOrDateSchema,
  productIds: z.array(z.string().length(21)).optional(),
});

export const storefrontResolveCombinationInputSchema = z.object({
  productId: z.string().length(21),
  quantity: z.number().int().min(1),
  startDate: dateTimeOrDateSchema,
  endDate: dateTimeOrDateSchema,
  selectedAttributes: z.record(z.string(), z.string()).optional(),
});

export const storefrontCartResolveInputSchema = z.object({
  lines: z
    .array(
      z.object({
        lineId: z.string().min(1).max(128),
        productId: z.string().length(21),
        quantity: z.number().int().min(1),
        startDate: dateTimeOrDateSchema,
        endDate: dateTimeOrDateSchema,
        selectedAttributes: z.record(z.string(), z.string()).optional(),
      }),
    )
    .max(100),
});

export const storefrontAvailabilityRouteQuerySchema = z.object({
  startDate: dateTimeOrDateSchema,
  endDate: dateTimeOrDateSchema,
  productIds: z.string().nullish(),
});

export const dashboardReservationPollInputSchema = z.object({});

export const dashboardReservationTimelinePeriodInputSchema = z
  .object({
    storeId: z.string().min(1).max(128),
    startDate: dateTimeOrDateSchema,
    endDate: dateTimeOrDateSchema,
  })
  .refine((period) => new Date(period.startDate) <= new Date(period.endDate), {
    message: "startDate must not be after endDate",
  });

const reservationTimelineDeliverySchema = z.object({
  address: z.string().nullable(),
  city: z.string().nullable(),
  postalCode: z.string().nullable(),
  country: z.string().nullable(),
});

export const dashboardReservationCalendarPeriodEntrySchema = z.object({
  id: z.string(),
  number: z.string(),
  status: reservationStatusSchema.nullable(),
  startDate: z.date(),
  endDate: z.date(),
  subtotalAmount: z.string(),
  depositAmount: z.string(),
  totalAmount: z.string(),
  outboundMethod: z.string(),
  returnMethod: z.string(),
  deliveryAddress: z.string().nullable(),
  deliveryCity: z.string().nullable(),
  deliveryPostalCode: z.string().nullable(),
  deliveryCountry: z.string().nullable(),
  returnAddress: z.string().nullable(),
  returnCity: z.string().nullable(),
  returnPostalCode: z.string().nullable(),
  returnCountry: z.string().nullable(),
  customer: z
    .object({
      id: z.string(),
      firstName: z.string(),
      lastName: z.string(),
    })
    .nullable(),
  items: z.array(
    z.object({
      id: z.string(),
      quantity: z.number(),
      productSnapshot: z
        .object({
          name: z.string(),
          images: z.array(z.string()).nullish(),
        })
        .nullable(),
      product: z
        .object({
          id: z.string(),
          name: z.string(),
          images: z.array(z.string()).nullable(),
          displayOrder: z.number().nullable(),
        })
        .nullable(),
    }),
  ),
});

export const dashboardReservationPlanningTimelineEntrySchema = z.object({
  id: z.string(),
  productId: z.string(),
  number: z.string(),
  status: reservationStatusSchema.nullable(),
  startDate: z.date(),
  endDate: z.date(),
  customerId: z.string().nullable(),
  customerName: z.string(),
  subtotalAmount: z.string(),
  depositAmount: z.string(),
  totalAmount: z.string(),
  quantity: z.number(),
  assignedUnitIds: z.array(z.string()),
  items: z.array(
    z.object({
      productId: z.string(),
      name: z.string(),
      quantity: z.number(),
      imageUrl: z.string().nullable(),
    }),
  ),
  outboundDelivery: reservationTimelineDeliverySchema.nullable(),
  returnDelivery: reservationTimelineDeliverySchema.nullable(),
});

export const dashboardReservationsListInputSchema = z.object({
  status: z
    .enum(["all", "pending", "confirmed", "ongoing", "completed", "cancelled", "rejected", "quote"])
    .optional(),
  period: z.enum(["today", "week", "month"]).optional(),
  operation: z.enum(["departure", "return"]).optional(),
  paymentMethod: z.enum(["stripe", "cash", "card", "transfer", "check", "other"]).optional(),
  limit: z.number().int().min(1).max(500).optional(),
  search: z.string().max(100).optional(),
  sort: z.enum(["startDate", "amount", "status", "number"]).optional(),
  sortDirection: z.enum(["asc", "desc"]).optional(),
  page: z.number().int().min(1).optional(),
  pageSize: z.number().int().min(1).max(100).optional(),
});

export const dashboardReservationGetByIdInputSchema = z.object({
  reservationId: z.string().length(21),
});

export const dashboardReservationUpdateNotesInputSchema = z.object({
  reservationId: z.string().length(21),
  notes: z.string().max(100000).default(""),
});

export const dashboardReservationUpdateStatusInputSchema = z.object({
  reservationId: z.string().length(21),
  status: reservationStatusSchema,
  rejectionReason: z.string().max(2000).optional(),
});

export const dashboardReservationCancelInputSchema = z.object({
  reservationId: z.string().length(21),
});

export const dashboardReservationGetAvailableUnitsInputSchema = z.object({
  reservationItemId: z.string().length(21),
});

export const dashboardReservationAssignUnitsInputSchema = z.object({
  reservationItemId: z.string().length(21),
  unitIds: z.array(z.string().length(21)).max(500),
  overrideTurnoverBuffer: z.boolean().optional(),
});

export const dashboardReservationRequestPaymentInputSchema = z.object({
  reservationId: z.string().length(21),
  payload: z.object({
    type: z.enum(["rental", "deposit", "custom"]),
    amount: z.number().min(0.5).optional(),
    channels: z.object({
      email: z.boolean(),
      sms: z.boolean(),
    }),
    customMessage: z.string().max(5000).optional(),
  }),
});

export const dashboardReservationGetPaymentMethodInputSchema = z.object({
  reservationId: z.string().length(21),
});

export const dashboardReservationRecordPaymentInputSchema = z.object({
  reservationId: z.string().length(21),
  payload: z.object({
    type: z.enum(["rental", "deposit", "deposit_return", "damage", "adjustment"]),
    amount: z.number(),
    method: z.enum(["cash", "card", "transfer", "check", "other"]),
    paidAt: z.union([dateTimeOrDateSchema, z.date()]).optional(),
    notes: z.string().max(10000).optional(),
  }),
});

export const dashboardReservationRefundManualPaymentInputSchema = z.object({
  reservationId: z.string().length(21),
  payload: z.object({
    paymentId: z.string().length(21),
    amount: z.number().min(0.01),
    method: z.enum(["cash", "card", "transfer", "check", "other"]),
    notes: z.string().max(10000).optional(),
  }),
});

export const dashboardReservationDeletePaymentInputSchema = z.object({
  paymentId: z.string().length(21),
});

export const dashboardReservationReturnDepositInputSchema = z.object({
  reservationId: z.string().length(21),
  payload: z.object({
    amount: z.number().min(0.01),
    method: z.enum(["cash", "card", "transfer", "check", "other"]),
    notes: z.string().max(10000).optional(),
  }),
});

export const dashboardReservationRecordDamageInputSchema = z.object({
  reservationId: z.string().length(21),
  payload: z.object({
    amount: z.number().min(0.01),
    method: z.enum(["cash", "card", "transfer", "check", "other"]),
    notes: z.string().max(10000),
  }),
});

export const dashboardReservationCreateDepositHoldInputSchema = z.object({
  reservationId: z.string().length(21),
});

export const dashboardReservationCaptureDepositHoldInputSchema = z.object({
  reservationId: z.string().length(21),
  payload: z.object({
    amount: z.number().min(0.01),
    reason: z.string().trim().min(1).max(10000),
  }),
});

export const dashboardReservationReleaseDepositHoldInputSchema = z.object({
  reservationId: z.string().length(21),
});

export const dashboardReservationSendReservationEmailInputSchema = z.object({
  reservationId: z.string().length(21),
  payload: z.object({
    templateId: z.string().trim().min(1).max(100),
    customSubject: z.string().max(500).optional(),
    customMessage: z.string().max(100000).optional(),
  }),
});

export const dashboardReservationGetEmailRenderContextInputSchema = z.object({
  reservationId: z.string().length(21),
});

export const dashboardReservationSendModificationEmailInputSchema = z.object({
  reservationId: z.string().length(21),
  payload: z
    .object({
      previousStartDate: z.union([dateTimeOrDateSchema, z.date()]).optional(),
      previousEndDate: z.union([dateTimeOrDateSchema, z.date()]).optional(),
    })
    .optional(),
});

export const dashboardReservationSendAccessLinkInputSchema = z.object({
  reservationId: z.string().length(21),
  payload: z
    .object({
      customMessage: z.string().max(100000).optional(),
    })
    .optional(),
});

export const dashboardReservationSendAccessLinkSmsInputSchema = z.object({
  reservationId: z.string().length(21),
});

export const dashboardReservationUpdateReservationInputSchema = z.object({
  reservationId: z.string().length(21),
  payload: z.object({
    startDate: z.union([dateTimeOrDateSchema, z.date()]).optional(),
    endDate: z.union([dateTimeOrDateSchema, z.date()]).optional(),
    notifyCustomerByEmail: z.boolean().optional(),
    tulipInsuranceOptIn: z.boolean().optional(),
    overrideTurnoverBuffer: z.boolean().optional(),
    delivery: z
      .object({
        outbound: z.object({
          method: z.enum(["store", "address"]),
          locationId: z.string().length(21).nullable().optional(),
          address: z.string().max(1000).optional(),
          city: z.string().max(255).optional(),
          postalCode: z.string().max(20).optional(),
          country: z.string().max(2).optional(),
          latitude: z.number().optional(),
          longitude: z.number().optional(),
        }),
        return: z.object({
          method: z.enum(["store", "address"]),
          locationId: z.string().length(21).nullable().optional(),
          address: z.string().max(1000).optional(),
          city: z.string().max(255).optional(),
          postalCode: z.string().max(20).optional(),
          country: z.string().max(2).optional(),
          latitude: z.number().optional(),
          longitude: z.number().optional(),
        }),
      })
      .optional(),
    items: z
      .array(
        z.object({
          id: z.string().length(21).optional(),
          productId: z.string().length(21).nullable().optional(),
          quantity: z.number().int().min(1),
          unitPrice: z.number(),
          depositPerUnit: z.number().min(0),
          isManualPrice: z.boolean().optional(),
          pricingMode: z.enum(["hour", "day", "week"]).optional(),
          productSnapshot: z.object({
            name: z.string().trim().min(1).max(500),
            description: z.string().max(100000).nullable().optional(),
            images: z.array(z.string().max(2048)).optional(),
          }),
        }),
      )
      .optional(),
  }),
});

export const dashboardReservationPreviewTulipQuoteInputSchema = z.object({
  reservationId: z.string().length(21),
  payload: z.object({
    startDate: z.union([dateTimeOrDateSchema, z.date()]),
    endDate: z.union([dateTimeOrDateSchema, z.date()]),
    tulipInsuranceOptIn: z.boolean().optional(),
    items: z
      .array(
        z.object({
          productId: z.string().length(21).nullable().optional(),
          quantity: z.number().int().min(1),
        }),
      )
      .max(500),
  }),
});

export const dashboardReservationPreviewManualTulipQuoteInputSchema = z.object({
  payload: z.object({
    customerId: z.string().length(21).optional(),
    newCustomer: z
      .object({
        email: customerEmailSchema,
        firstName: z.string().trim().min(1).max(200),
        lastName: z.string().trim().min(1).max(200),
        phone: z.string().trim().max(50).optional(),
      })
      .optional(),
    startDate: z.union([dateTimeOrDateSchema, z.date()]),
    endDate: z.union([dateTimeOrDateSchema, z.date()]),
    tulipInsuranceOptIn: z.boolean().optional(),
    items: z
      .array(
        z.object({
          productId: z.string().length(21),
          quantity: z.number().int().min(1),
        }),
      )
      .max(500),
  }),
});

export const dashboardReservationCreateManualReservationInputSchema = z.object({
  payload: z.object({
    customerId: z.string().length(21).optional(),
    newCustomer: z
      .object({
        email: customerEmailSchema,
        firstName: z.string().trim().min(1).max(200),
        lastName: z.string().trim().min(1).max(200),
        phone: z.string().trim().max(50).optional(),
      })
      .optional(),
    startDate: z.union([dateTimeOrDateSchema, z.date()]),
    endDate: z.union([dateTimeOrDateSchema, z.date()]),
    items: z.array(
      z.object({
        productId: z.string().length(21),
        quantity: z.number().int().min(1),
        selectedAttributes: z.record(z.string(), z.string()).optional(),
        priceOverride: z
          .object({
            unitPrice: z.number().min(0),
          })
          .optional(),
      }),
    ),
    customItems: z
      .array(
        z.object({
          name: z.string().trim().min(1).max(500),
          description: z.string().max(100000),
          unitPrice: z.number(),
          deposit: z.number().min(0),
          quantity: z.number().int().min(1),
          pricingMode: z.enum(["hour", "day", "week"]),
        }),
      )
      .optional(),
    delivery: z
      .object({
        outbound: z.object({
          method: z.enum(["store", "address"]),
          locationId: z.string().length(21).nullable().optional(),
          address: z.string().max(1000).optional(),
          city: z.string().max(255).optional(),
          postalCode: z.string().max(20).optional(),
          country: z.string().max(2).optional(),
          latitude: z.number().min(-90).max(90).optional(),
          longitude: z.number().min(-180).max(180).optional(),
        }),
        return: z.object({
          method: z.enum(["store", "address"]),
          locationId: z.string().length(21).nullable().optional(),
          address: z.string().max(1000).optional(),
          city: z.string().max(255).optional(),
          postalCode: z.string().max(20).optional(),
          country: z.string().max(2).optional(),
          latitude: z.number().min(-90).max(90).optional(),
          longitude: z.number().min(-180).max(180).optional(),
        }),
      })
      .optional(),
    internalNotes: z.string().max(100000).optional(),
    discountAmount: z.number().min(0).optional(),
    depositOverride: z.number().min(0).optional(),
    tulipInsuranceOptIn: z.boolean().optional(),
    sendConfirmationEmail: z.boolean().optional(),
    sendAsQuote: z.boolean().optional(),
    allowOverbooking: z.boolean().optional(),
  }),
});

export const updateStoreLegalInputSchema = z.object({
  cgv: z.string().max(100000, "errors.invalidData").optional(),
  legalNotice: z.string().max(100000, "errors.invalidData").optional(),
  includeFullCgvInContract: z.boolean().optional(),
});

const s3UrlSchema = z
  .string()
  .refine(
    (url) => !url.startsWith("data:"),
    "Base64 images are not allowed. Please upload images to S3.",
  )
  .refine((url) => isValidImageUrl(url), "Invalid image URL. Must be a valid S3 URL.");

export const updateStoreAppearanceInputSchema = z.object({
  logoUrl: z.union([s3UrlSchema, z.literal(""), z.null()]).optional(),
  darkLogoUrl: z.union([s3UrlSchema, z.literal(""), z.null()]).optional(),
  theme: z
    .object({
      mode: z.enum(["light", "dark"]),
      primaryColor: z.string().regex(/^#[0-9A-Fa-f]{6}$/, "Invalid hex color"),
      heroImages: z.array(s3UrlSchema).max(5).optional(),
      catalogBrowseMode: z.enum(["products", "categories"]).optional(),
      maxDiscountPercent: z.number().int().min(0).max(100).nullish(),
    })
    .optional(),
});

export const dashboardIntegrationsGetTulipStateInputSchema = z.object({});

export const dashboardIntegrationsGetTulipProductStateInputSchema = z.object({
  productId: z.string().length(21),
});

export const dashboardIntegrationsListCatalogInputSchema = z.object({});

export const dashboardIntegrationsListCategoryInputSchema = z.object({
  category: z.string().trim().min(1).max(60),
});

export const dashboardIntegrationsGetDetailInputSchema = z.object({
  integrationId: z.string().trim().min(1).max(60),
});

export const dashboardIntegrationsSetEnabledInputSchema = z.object({
  integrationId: z.string().trim().min(1).max(60),
  enabled: z.boolean(),
});

export const dashboardIntegrationsGetCalendarStateInputSchema = z.object({});

export const dashboardIntegrationsUpdateGoogleCalendarSettingsInputSchema = z.object({
  syncPendingReservations: z.boolean(),
  cancelledReservationBehavior: z.enum(["show", "hide"]),
});

export const dashboardIntegrationsResyncGoogleCalendarInputSchema = z.object({});

export const dashboardIntegrationsDisconnectGoogleCalendarInputSchema = z.object({
  deleteEvents: z.boolean().default(false),
});

export const dashboardIntegrationsConnectTulipInputSchema = z.object({
  renterUid: z.string().trim().min(1).max(120),
});

export const dashboardIntegrationsUpdateTulipConfigurationInputSchema = z.object({
  publicMode: z.enum(["required", "optional", "no_public"]),
});

export const dashboardIntegrationsUpsertTulipProductMappingInputSchema = z.object({
  productId: z.string().length(21),
  tulipProductId: z.string().trim().min(1).max(50).nullable(),
});

const tulipProductTypeSchema = z.string().trim().min(1).max(80);
const tulipProductSubtypeSchema = z.string().trim().min(1).max(80);

const tulipPurchasedDateSchema = z.union([
  z.string().datetime({ offset: true }),
  z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
]);

export const dashboardIntegrationsPushTulipProductUpdateInputSchema = z.object({
  productId: z.string().length(21),
  title: z.string().trim().max(255).nullable().optional(),
  productType: tulipProductTypeSchema.nullable().optional(),
  productSubtype: tulipProductSubtypeSchema.nullable().optional(),
  purchasedDate: tulipPurchasedDateSchema.nullable().optional(),
  brand: z.string().trim().max(120).nullable().optional(),
  model: z.string().trim().max(120).nullable().optional(),
  valueExcl: z.number().min(0).max(1_000_000).nullable().optional(),
  margin: z.number().min(0).max(1_000_000).nullable().optional(),
});

export const dashboardIntegrationsCreateTulipProductInputSchema = z.object({
  productId: z.string().length(21),
  title: z.string().trim().max(255).nullable().optional(),
  productType: tulipProductTypeSchema.nullable().optional(),
  productSubtype: tulipProductSubtypeSchema.nullable().optional(),
  purchasedDate: tulipPurchasedDateSchema.nullable().optional(),
  brand: z.string().trim().max(120).nullable().optional(),
  model: z.string().trim().max(120).nullable().optional(),
  valueExcl: z.number().min(0).max(1_000_000).nullable().optional(),
  margin: z.number().min(0).max(1_000_000).nullable().optional(),
});

export const dashboardIntegrationsDisconnectTulipInputSchema = z.object({});

export const addressAutocompleteInputSchema = z.object({
  query: z.string().trim().min(3).max(200),
});

export const addressResolveInputSchema = z.object({
  query: z.string().trim().min(3).max(200),
});

export const addressDetailsInputSchema = z.object({
  placeId: z.string().trim().min(1).max(255),
});

export const addressReverseGeocodeInputSchema = z.object({
  latitude: z.number().min(-90).max(90),
  longitude: z.number().min(-180).max(180),
});

export const routeDistanceInputSchema = z.object({
  originLatitude: z.number().min(-90).max(90),
  originLongitude: z.number().min(-180).max(180),
  destinationLatitude: z.number().min(-90).max(90),
  destinationLongitude: z.number().min(-180).max(180),
});

export const reservationSignInputSchema = z.object({
  reservationId: z.string().length(21),
});

export type StorefrontAvailabilityInput = z.infer<typeof storefrontAvailabilityInputSchema>;
export type StorefrontResolveCombinationInput = z.infer<
  typeof storefrontResolveCombinationInputSchema
>;
export type StorefrontCartResolveInput = z.infer<typeof storefrontCartResolveInputSchema>;
export type DashboardReservationPollInput = z.infer<typeof dashboardReservationPollInputSchema>;
export type DashboardReservationTimelinePeriodInput = z.infer<
  typeof dashboardReservationTimelinePeriodInputSchema
>;
export type ReservationCalendarPeriodEntry = z.infer<
  typeof dashboardReservationCalendarPeriodEntrySchema
>;
export type ReservationPlanningTimelineEntry = z.infer<
  typeof dashboardReservationPlanningTimelineEntrySchema
>;
export type DashboardReservationsListInput = z.infer<typeof dashboardReservationsListInputSchema>;
export type DashboardReservationGetByIdInput = z.infer<
  typeof dashboardReservationGetByIdInputSchema
>;
export type DashboardReservationUpdateNotesInput = z.infer<
  typeof dashboardReservationUpdateNotesInputSchema
>;
export type DashboardReservationUpdateStatusInput = z.infer<
  typeof dashboardReservationUpdateStatusInputSchema
>;
export type DashboardReservationCancelInput = z.infer<typeof dashboardReservationCancelInputSchema>;
export type DashboardReservationGetAvailableUnitsInput = z.infer<
  typeof dashboardReservationGetAvailableUnitsInputSchema
>;
export type DashboardReservationAssignUnitsInput = z.infer<
  typeof dashboardReservationAssignUnitsInputSchema
>;
export type DashboardReservationRequestPaymentInput = z.infer<
  typeof dashboardReservationRequestPaymentInputSchema
>;
export type DashboardReservationGetPaymentMethodInput = z.infer<
  typeof dashboardReservationGetPaymentMethodInputSchema
>;
export type DashboardReservationRecordPaymentInput = z.infer<
  typeof dashboardReservationRecordPaymentInputSchema
>;
export type DashboardReservationRefundManualPaymentInput = z.infer<
  typeof dashboardReservationRefundManualPaymentInputSchema
>;
export type DashboardReservationDeletePaymentInput = z.infer<
  typeof dashboardReservationDeletePaymentInputSchema
>;
export type DashboardReservationReturnDepositInput = z.infer<
  typeof dashboardReservationReturnDepositInputSchema
>;
export type DashboardReservationRecordDamageInput = z.infer<
  typeof dashboardReservationRecordDamageInputSchema
>;
export type DashboardReservationCreateDepositHoldInput = z.infer<
  typeof dashboardReservationCreateDepositHoldInputSchema
>;
export type DashboardReservationCaptureDepositHoldInput = z.infer<
  typeof dashboardReservationCaptureDepositHoldInputSchema
>;
export type DashboardReservationReleaseDepositHoldInput = z.infer<
  typeof dashboardReservationReleaseDepositHoldInputSchema
>;
export type DashboardReservationSendReservationEmailInput = z.infer<
  typeof dashboardReservationSendReservationEmailInputSchema
>;
export type DashboardReservationGetEmailRenderContextInput = z.infer<
  typeof dashboardReservationGetEmailRenderContextInputSchema
>;
export type DashboardReservationSendModificationEmailInput = z.infer<
  typeof dashboardReservationSendModificationEmailInputSchema
>;
export type DashboardReservationSendAccessLinkInput = z.infer<
  typeof dashboardReservationSendAccessLinkInputSchema
>;
export type DashboardReservationSendAccessLinkSmsInput = z.infer<
  typeof dashboardReservationSendAccessLinkSmsInputSchema
>;
export type DashboardReservationUpdateReservationInput = z.infer<
  typeof dashboardReservationUpdateReservationInputSchema
>;
export type DashboardReservationCreateManualReservationInput = z.infer<
  typeof dashboardReservationCreateManualReservationInputSchema
>;
export type UpdateStoreLegalInput = z.infer<typeof updateStoreLegalInputSchema>;
export type UpdateStoreAppearanceInput = z.infer<typeof updateStoreAppearanceInputSchema>;
export type DashboardIntegrationsGetTulipStateInput = z.infer<
  typeof dashboardIntegrationsGetTulipStateInputSchema
>;
export type DashboardIntegrationsListCatalogInput = z.infer<
  typeof dashboardIntegrationsListCatalogInputSchema
>;
export type DashboardIntegrationsListCategoryInput = z.infer<
  typeof dashboardIntegrationsListCategoryInputSchema
>;
export type DashboardIntegrationsGetDetailInput = z.infer<
  typeof dashboardIntegrationsGetDetailInputSchema
>;
export type DashboardIntegrationsSetEnabledInput = z.infer<
  typeof dashboardIntegrationsSetEnabledInputSchema
>;
export type DashboardIntegrationsGetCalendarStateInput = z.infer<
  typeof dashboardIntegrationsGetCalendarStateInputSchema
>;
export type DashboardIntegrationsUpdateGoogleCalendarSettingsInput = z.infer<
  typeof dashboardIntegrationsUpdateGoogleCalendarSettingsInputSchema
>;
export type DashboardIntegrationsResyncGoogleCalendarInput = z.infer<
  typeof dashboardIntegrationsResyncGoogleCalendarInputSchema
>;
export type DashboardIntegrationsDisconnectGoogleCalendarInput = z.infer<
  typeof dashboardIntegrationsDisconnectGoogleCalendarInputSchema
>;
export type DashboardIntegrationsConnectTulipInput = z.infer<
  typeof dashboardIntegrationsConnectTulipInputSchema
>;
export type DashboardIntegrationsUpdateTulipConfigurationInput = z.infer<
  typeof dashboardIntegrationsUpdateTulipConfigurationInputSchema
>;
export type DashboardIntegrationsUpsertTulipProductMappingInput = z.infer<
  typeof dashboardIntegrationsUpsertTulipProductMappingInputSchema
>;
export type DashboardIntegrationsPushTulipProductUpdateInput = z.infer<
  typeof dashboardIntegrationsPushTulipProductUpdateInputSchema
>;
export type DashboardIntegrationsCreateTulipProductInput = z.infer<
  typeof dashboardIntegrationsCreateTulipProductInputSchema
>;
export type DashboardIntegrationsDisconnectTulipInput = z.infer<
  typeof dashboardIntegrationsDisconnectTulipInputSchema
>;
export type AddressAutocompleteInput = z.infer<typeof addressAutocompleteInputSchema>;
export type AddressResolveInput = z.infer<typeof addressResolveInputSchema>;
export type AddressDetailsInput = z.infer<typeof addressDetailsInputSchema>;
export type AddressReverseGeocodeInput = z.infer<typeof addressReverseGeocodeInputSchema>;
export type RouteDistanceInput = z.infer<typeof routeDistanceInputSchema>;
export type ReservationSignInput = z.infer<typeof reservationSignInputSchema>;
